import { nanoid } from 'nanoid';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import queryString from 'query-string';
import type { match as ReactRouterMatch } from 'react-router-dom';
import type { AnyAction } from 'redux';
import { delay } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import type { CallEffect, SelectEffect } from 'redux-saga/effects';

import { PART_STATUSES, PROCESSING_CATEGORIES } from '@frontend/api';
import { type LegacyFileTaskStatusData } from '@frontend/api';
import { createStandaloneToast, theme } from '@frontend/legacy-components';
import { UNITS } from '@frontend/partviewer/src/constants';
import type { PART_TYPES } from '@frontend/partviewer/src/constants';
import type { BodyJson } from '@frontend/partviewer/src/contracts/body';
import type { PartJson } from '@frontend/partviewer/src/contracts/part';
import type { TessJson } from '@frontend/partviewer/src/contracts/tess';
import { dxfService } from '@frontend/partviewer/src/loaders/dxf/service';
import type { UpdateVectorOperation } from '@frontend/partviewer/src/renderer/renderer-component';
import type { ViewerDataResponseJson } from '@frontend/partviewer/src/viewerDataService';

import partLibraryService, {
  type LegacyFileTaskStatusResponse,
} from 'services/partLibraryService';
import partService from 'services/partService';

import { inchesToMm } from 'utils/helpers';

import {
  selectorTool,
  types,
  vectorizationRenderer,
  saveVectorFile,
  vectorizationEditor,
} from './actions';
import { EDITOR_MODES, DEFAULT_UNITS, DEFAULT_SCALE } from './constants';
import {
  getDisplayUnits,
  getPresentHistoryUpdatedVectors,
  getPartMasterVersion,
  getPartUuid, //getPartFileUuid,
  getFutureHistoryUpdatedVectors,
  getThickness,
  getDisplayScale,
  getDXF,
  getPartFileName,
  getSaveFileName,
  getVectorizationStatus,
  VectorizationStatus,
} from './reducer';
import type { VectorEditorState } from './reducer';

type InitVectorFile = {
  url: string;
  partFileName: string;
  partFileUuid: string;
  partFileUnits: UNITS;
  partNumber: string;
  isExportControlled: boolean;
  isAssembly: boolean;
  revision: string;
  partUuid: string;
  partMasterVersion: string;
};

type VectorFile = {
  partFileName: string;
  isExportControlled: boolean;
  isAssembly: boolean;
  revision: string;
  partNumber: string;
  nativeUnits: UNITS;
  displayUnits: UNITS;
  partFileUuid: string;
  partMasterVersion: string;
  partUuid: string;
  displayScale: number;
  tess?: TessJson;
  id: string;
  url: string;
};

type InitStatusResponse = {
  status: {
    vectorization: LegacyFileTaskStatusData;
  };
};

const { toast } = createStandaloneToast({ theme });

const getEmptyPartModel = (): ViewerDataResponseJson => {
  return {
    assemblies: [],
    assemblyInstances: [],
    bodies: [],
    parts: [],
  };
};

// Later on can add features for accessing view states
// in CADtools (vectorizationRenderer version) can have "edit"/"view" state
// edit state is the main area to toggle + assign vectors
// view state allows user to see:
//    just export layers
//    all vectors with exports colored
//    original file colors
//    show deleted vectors (similar to current watchReceiveHideExcluded)

export default function* root() {
  yield takeLatest(
    types.RECEIVE_UNITS_SCALE_THICKNESS,
    watchReceiveUnitsScaleThickness,
  );
  yield takeLatest(
    types.EDITOR_RECEIVE_UPDATED_VECTORS,
    watchReceiveUpdatedVectors,
  );

  yield takeEvery(types.UNDO, watchUndo);
  yield takeEvery(types.REDO, watchRedo);

  yield takeLatest(
    types.RENDERER_RECEIVE_SERIALIZED_DXF,
    watchReceiveSerializedDXF,
  );
  // yield spawn(pageWorker);
}

export function* initVectorEditor({
  location,
}: {
  location: Location;
  match: ReactRouterMatch<Record<string, string>>;
}) {
  yield put(vectorizationRenderer.reset());

  const { part: partUuid, partFile: partFileUuid } = queryString.parse(
    location.search,
  ) as {
    part: string;
    partFile: string;
  };

  try {
    if (!partUuid) {
      throw Error('No Part provided');
    }
    if (!partFileUuid) {
      throw Error('No PartFile provided');
    }
    // Call to start backend tasks for conversion -- start polling for statuses
    const response: InitStatusResponse = yield call(
      partService.postVectorFileData,
      partFileUuid,
    );
    const status = response.status[PROCESSING_CATEGORIES.VECTORIZATION]
      .status as VectorizationStatus;

    // res.status is FINISHED when file has already been converted: if this is the case, no need to ping for status, just proceed to init
    // res.status IS IN_PROGRESS when file is in task tracker
    yield put(
      vectorizationRenderer.receiveVectorizationStatus({
        status,
      }),
    );

    // Call to status checker, when "FINISHED", this inits finalization
    yield call(periodicallyCheckStatus, { partFileUuid, location });
  } catch (error) {
    yield put(vectorizationRenderer.receiveError());
    console.error(error);
  }
}

function* periodicallyCheckStatus({
  partFileUuid,
  location,
}: {
  partFileUuid: string;
  location: Location;
}) {
  let status: VectorizationStatus = yield select(getVectorizationStatus);
  while (status !== PART_STATUSES.FINISHED) {
    try {
      const response: LegacyFileTaskStatusResponse = yield call(
        partService.checkStatuses,
        [partFileUuid],
      );
      status = response.statuses.processing[0][
        PROCESSING_CATEGORIES.VECTORIZATION
      ].status as VectorizationStatus;
      yield put(
        vectorizationRenderer.receiveVectorizationStatus({
          status,
        }),
      );
      if (status !== PART_STATUSES.FINISHED) {
        yield call(delay, 2000);
      }
    } catch (error) {
      console.error(error);
      yield call(delay, 2000);
    }
  }
  yield call(finalizeInitVectorEditor, { location });
}

const constructInitPayload = ({ obj }: { obj: VectorFile }) => {
  return {
    partFileName: obj.partFileName,
    isExportControlled: obj.isExportControlled,
    isAssembly: obj.isAssembly,
    revision: obj.revision,
    partNumber: obj.partNumber,
    nativeUnits: obj.nativeUnits,
    displayUnits: obj.displayUnits,
    partFileUuid: obj.partFileUuid,
    partMasterVersion: obj.partMasterVersion,
    partUuid: obj.partUuid,
    displayScale: obj.displayScale,
  };
};

// Part model will come from local processing
function constructPartModelFromVectorFile({
  vectorFile,
}: {
  vectorFile: VectorFile;
}): ViewerDataResponseJson {
  const model = getEmptyPartModel();

  const newPart: PartJson<PART_TYPES.DXF> = {
    partFileUuid: vectorFile.partFileUuid,
    name: vectorFile.partFileUuid,
    tess: vectorFile.tess,
  };

  const newBody: BodyJson = {
    id: vectorFile.id.toString(),
    name: vectorFile.id.toString(),
    partFileUuid: vectorFile.partFileUuid,
    isRoot: true,
    opacity: 1.0,
  };

  model.parts.push(newPart);
  model.bodies.push(newBody);

  return model;
}

function* finalizeInitVectorEditor({ location }: { location: Location }) {
  try {
    const { part: partUuid } = queryString.parse(location.search) as {
      part: string;
    };

    const init: InitVectorFile = yield call(
      partService.getVectorFileData,
      partUuid,
    );

    const dxfContent: string = yield fetch(init.url, { method: 'GET' }).then(
      (res) => res.text(),
    );
    const tess = dxfService.importDxfContent(init.partFileUuid, dxfContent);
    const dxfScale = dxfService.getScale(init.partFileUuid);
    const dxfUnits = dxfService.getUnits(init.partFileUuid);

    const vectorFile: VectorFile = {
      partFileName: init.partFileName,
      isExportControlled: init.isExportControlled ?? false,
      isAssembly: init.isAssembly ?? false,
      revision: init.revision ?? '',
      partNumber: init.partNumber ?? '',
      nativeUnits: dxfUnits || init.partFileUnits || DEFAULT_UNITS,
      displayUnits: dxfUnits || init.partFileUnits || DEFAULT_UNITS,
      partFileUuid: init.partFileUuid,
      partMasterVersion: init.partMasterVersion,
      partUuid: init.partUuid,
      displayScale: dxfScale || DEFAULT_SCALE,
      id: nanoid(),
      url: init.url,
      tess,
    };

    const payload = constructInitPayload({ obj: vectorFile });
    const model = constructPartModelFromVectorFile({ vectorFile });

    yield put(vectorizationRenderer.receiveViewerData({ viewerData: model }));
    yield put(vectorizationRenderer.init(payload));
  } catch (error) {
    yield put(vectorizationRenderer.receiveError());
    console.error(error);
  }
}

function* watchReceiveUnitsScaleThickness() {
  yield put(
    vectorizationRenderer.receiveEditorMode({
      editorMode: EDITOR_MODES.LAYERS,
    }),
  );
  yield put(selectorTool.enable());
  yield put(vectorizationEditor.finalizeSetScale());
  yield call(toast, {
    status: 'success',
    title: 'Scale and thickness saved.',
    duration: 3000,
  });
}

function* finishUpdatedVectors({
  historyCommand,
  inverse = false,
  keepSelected = false,
}: {
  historyCommand: VectorEditorState['historyUpdatedVectors']['present'];
  inverse?: boolean;
  keepSelected?: boolean;
}) {
  if (!keepSelected) {
    yield put(selectorTool.clearAllSelections());
  }
  yield put(
    vectorizationRenderer.receiveUpdatedVectors({
      updatedVectors: inverse
        ? historyCommand.inverse
        : historyCommand.operation,
    }),
  );
}

function* watchReceiveUpdatedVectors(
  action: AnyAction,
): Generator<
  SelectEffect | CallEffect,
  void,
  VectorEditorState['historyUpdatedVectors']['present']
> {
  const { updatedVectors } = action.payload as {
    updatedVectors: UpdateVectorOperation[];
  };
  let keepSelected = false;

  updatedVectors.forEach((updatedVector) => {
    if (updatedVector.visible === true) {
      keepSelected = true;
    }
  });

  const presentHistoryUpdatedVectors = yield select(
    getPresentHistoryUpdatedVectors,
  );
  yield call(finishUpdatedVectors, {
    historyCommand: presentHistoryUpdatedVectors,
    keepSelected,
  });
}

function* watchUndo(): Generator<
  SelectEffect | CallEffect,
  void,
  VectorEditorState['historyUpdatedVectors']['present']
> {
  // Play the inverse of the now future HistoryUpdatedVectors
  const futureHistoryUpdatedVectors = yield select(
    getFutureHistoryUpdatedVectors,
  );
  yield call(finishUpdatedVectors, {
    historyCommand: futureHistoryUpdatedVectors,
    inverse: true,
  });
}

function* watchRedo(): Generator<
  SelectEffect | CallEffect,
  void,
  VectorEditorState['historyUpdatedVectors']['present']
> {
  // Re-Play the action of the now present HistoryUpdatedVectors
  const presentHistoryUpdatedVectors = yield select(
    getPresentHistoryUpdatedVectors,
  );
  yield call(finishUpdatedVectors, {
    historyCommand: presentHistoryUpdatedVectors,
  });
}

function* watchReceiveSerializedDXF() {
  const partFileName: string = yield select(getPartFileName);
  const saveFileName: string = yield select(getSaveFileName);
  const thickness: number = yield select(getThickness);
  const displayScale: number = yield select(getDisplayScale);
  const displayUnits: UNITS = yield select(getDisplayUnits);
  // thickness must post to the backend in MM
  const useThickness =
    displayUnits === UNITS.MM ? thickness : inchesToMm(thickness);

  const partUuid: string = yield select(getPartUuid);
  //const partFileUuid: string = yield select(getPartFileUuid);
  const partMasterVersion: string = yield select(getPartMasterVersion);

  // Set units + scale of dxfService singleton instance
  //Not setting units in dxf because this may impact backend's attempt to scale
  //dxfService.setUnits(partFileUuid, displayUnits);
  //dxfService.setScale(partFileUuid, displayScale); //does not work. Passing scale to transform_dxf field in formData

  // serialize part
  const dxfBlob: Blob | null = yield select(getDXF);

  if (!dxfBlob) {
    throw Error('No DXF file created');
  }

  try {
    const formData = new FormData();
    formData.append('original', dxfBlob, saveFileName ?? partFileName);
    formData.append('units', displayUnits);
    if (thickness) {
      formData.append('thickness', useThickness as unknown as string);
    }
    formData.append('part_master_version', partMasterVersion ?? '');
    //When defined, backend will transform the dxf and apply scale
    //* Transform is required to normalize the handle and ownerHandle values of the filtered DXF created by vector editor
    //* Transform is also required to apply the scale.
    formData.append('scale_dxf', `${displayScale ?? 1.0}`);

    yield call(partLibraryService.supportingFileUpload, {
      formData,
      uuid: partUuid,
    });

    yield put(saveVectorFile.success());
  } catch (error) {
    yield call(toast, {
      status: 'error',
      title: 'File export failed.',
      duration: 3000,
    });
    yield put(saveVectorFile.error(error));
  }
}
